
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FolderPlus, FileText, HelpCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CreateSectionProps {
  onSectionCreated: (section: { id: string; name: string; section_type: string }) => void;
  organizationId: string;
}

type SectionType = 'DOCUMENTS' | 'FAQ';

export const CreateSection = ({ onSectionCreated, organizationId }: CreateSectionProps) => {
  const [newSectionName, setNewSectionName] = useState("");
  const [sectionType, setSectionType] = useState<SectionType>('DOCUMENTS');
  const [isLoading, setIsLoading] = useState(false);

  const createSection = async () => {
    if (!newSectionName.trim()) {
      toast.error('Please enter a section name');
      return;
    }

    setIsLoading(true);
    try {
      const memberId = sessionStorage.getItem('organization_member_id');

      // Verify we have the required IDs
      if (!organizationId || !memberId) {
        throw new Error('Missing organization or member ID');
      }

      const { data, error } = await supabase
        .from('document_sections')
        .insert({
          name: newSectionName.trim(),
          organization_id: organizationId,
          created_by: memberId, // Use organization member ID instead of auth user ID
          section_type: sectionType
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Section created successfully');
      setNewSectionName("");
      setSectionType('DOCUMENTS');
      onSectionCreated(data);
    } catch (error: any) {
      console.error('Error creating section:', error);
      toast.error(error.message || 'Failed to create section');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-muted/20">
      <div>
        <Label className="text-sm font-medium">Section Type</Label>
        <RadioGroup
          value={sectionType}
          onValueChange={(value) => setSectionType(value as SectionType)}
          className="flex gap-6 mt-2"
          disabled={isLoading}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="DOCUMENTS" id="documents" />
            <Label htmlFor="documents" className="flex items-center gap-2 cursor-pointer">
              <FileText className="h-4 w-4" />
              Documents
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="FAQ" id="faq" />
            <Label htmlFor="faq" className="flex items-center gap-2 cursor-pointer">
              <HelpCircle className="h-4 w-4" />
              FAQ
            </Label>
          </div>
        </RadioGroup>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          type="text"
          placeholder={`New ${sectionType.toLowerCase()} section name`}
          value={newSectionName}
          onChange={(e) => setNewSectionName(e.target.value)}
          className="max-w-xs"
          disabled={isLoading}
        />
        <Button
          onClick={createSection}
          className="flex items-center gap-2"
          disabled={isLoading}
        >
          <FolderPlus className="h-4 w-4" />
          Add Section
        </Button>
      </div>
    </div>
  );
};
